import React, { useEffect, createContext, useContext } from "react";
import { subscriptionFetch } from "@/redux/slice/profile/subscription";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useAuth } from "./auth-context";

interface SubscriptionPlan {
  isSubscribed: boolean;
  subscription?: {
    planId: string;
    billingCycle: string;
    endDate: string | undefined;
    partnerSessions: {
      entitledPerWeek: number;
      remainingThisWeek: number;
    };
  };
}

interface SubscriptionState {
  data: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
}

interface SubscriptionContextType {
  subscription: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
  refetchSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscription: null,
  loading: false,
  error: null,
  refetchSubscription: () => {},
});

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

interface AuthContextType {
  token: string | null;
}

export const SubscriptionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useAppDispatch();

  const { data, loading, error } = useAppSelector(
    (state) => state.subscription
  );

  const { token } = useAuth();

  const refetchSubscription = () => {
    if (token) {
      dispatch(subscriptionFetch({ token }));
    }
  };

  useEffect(() => {
    if (token) {
      refetchSubscription();
    }
  }, [token, dispatch]);

  const value: SubscriptionContextType = {
    subscription: data,
    loading,
    error,
    refetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
